import { useEffect, useRef, useMemo, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useLocation, NavLink, Outlet } from 'react-router-dom';
import {
  IconLoader,
  Table,
  useQueryState,
  Filters,
  useFilters,
  usePrevious,
  IconExpand,
  useScroll,
  useGetList,
  SelectItemProps,
  ButtonSecondary,
  ButtonSize,
  IconDuplicate,
  Popup,
  IconTrash,
  PermissionEnum,
  formatSearchParams,
  SchemaFilterType,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { selectList, clearTariffsListState } from './store/slices/list';
import {
  useLazyGetTariffsListQuery,
  useDuplicateTariffMutation,
  useDeleteTariffMutation,
  useToggleTariffDefaultMutation,
  useChangeTariffStatusMutation,
} from './store/services';
import PublishSidepanel from '../../components/tariffs/publish-sidepanel';
import ArchiveSidepanel from '../../components/tariffs/archive-sidepanel';
import { TariffItem, TariffType, TariffStatus, TariffAction } from './types';
import {
  tariffsListColumns,
  tariffsListSchema,
  tariffsListFiltersSchema,
  tariffsListColumnsLabels,
} from './variables/list';
import { TariffsRoutes } from '../../routes';
import { clearTariffPageState } from './store/slices/tariff-page';

import cn from './tariffs.module.css';

const formatTariffsList = (
  list: TariffItem[],
  riskScoreRanges: SelectItemProps[],
  setDefaultTariff: ({
    popupVisible,
    id,
  }: {
    popupVisible: boolean;
    id: string;
  }) => void,
): TariffItem[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let tariffStatus;
    let tariffStatusClassName = '';

    if (result.status === TariffStatus.DRAFT) {
      tariffStatus = 'Draft';
    } else if (result.status === TariffStatus.ARCHIVED) {
      tariffStatus = 'Archived';
      tariffStatusClassName = cn.acrhivedStatus;
    } else if (result.status === TariffStatus.PUBLISHED) {
      tariffStatus = 'Published';
      tariffStatusClassName = cn.publishedStatus;
    }

    let defaultContent;

    if (result.isDefault) {
      defaultContent = (
        <div className={cn.tariffsDefault}>
          <div className={cn.hintText}>
            Default tariff is automatically assigned for a combination of client
            type and risk scoring category.
          </div>
          Default
        </div>
      );
    } else if (!result.isDefault && result.status === TariffStatus.PUBLISHED) {
      defaultContent = (
        <button
          type="button"
          className={cn.makeDefault}
          onClick={() =>
            setDefaultTariff({ popupVisible: true, id: result.id })
          }
        >
          Make as default
        </button>
      );
    }

    const render: Record<string, React.JSX.Element> = {
      name: (
        <div
          className={`${cn.tariffsName} ${
            result.status === TariffStatus.DRAFT ? cn.tariffsRowDisabled : ''
          }`}
        >
          <span>{result?.name}</span>
          {defaultContent}
        </div>
      ),
      riskScoreRangeId: (
        <div
          className={`${cn.tariffsName} ${
            result.status === TariffStatus.DRAFT ? cn.tariffsRowDisabled : ''
          }`}
        >
          {
            riskScoreRanges?.find(
              (range) => range.value === result?.riskScoreRangeId,
            )?.name
          }
        </div>
      ),
      type: (
        <div
          className={`${cn.tariffsName} ${
            result.status === TariffStatus.DRAFT ? cn.tariffsRowDisabled : ''
          }`}
        >
          {result?.type === TariffType.INDIVIDUAL ? 'Individual' : 'Corporate'}
        </div>
      ),
      status: (
        <div
          className={`${cn.tariffsName} ${
            cn.tariffStatus
          } ${tariffStatusClassName} ${
            result.status === TariffStatus.DRAFT ? cn.tariffsRowDisabled : ''
          }`}
        >
          {tariffStatus}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const TariffsList = (): React.JSX.Element => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    tariffs: {
      list: { selectedList, pagination, tariffs, errorsList },
      tariffPage: { generalInfo },
    },
    configurationDictionaries: { activePermissions, riskScoreRanges },
  } = useAppSelector((state) => state);
  const [currentTariff, setCurrentTariff] = useState<TariffItem | undefined>();
  const [selectedTariffs, setSelectedTariffs] = useState<TariffItem[]>([]);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [publishPopupVisible, setPublishPopupVisible] = useState(false);
  const [archivePopupVisible, setArchivePopupVisible] = useState(false);
  const [defaultTariff, setDefaultTariff] = useState({
    popupVisible: false,
    id: '',
  });

  const dispatch = useAppDispatch();
  const { search } = useLocation();

  const { filters, onFilterChange } = useQueryState(tariffsListSchema);

  const prevFilters = usePrevious(filters);
  const prevPagination = usePrevious(pagination);

  const [getList, { isFetching, isLoading }] = useLazyGetTariffsListQuery({
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [duplicate, { isLoading: requestInProgress }] =
    useDuplicateTariffMutation();
  const [
    deleteTariff,
    { isLoading: deleteInProgress, isSuccess: deleteIsSuccess },
  ] = useDeleteTariffMutation();
  const [
    makeDefault,
    { isLoading: defaultInProgress, isSuccess: defaultIsSuccess },
  ] = useToggleTariffDefaultMutation();

  const { getListAction, onFilter, setPage, errorList } =
    useGetList<TariffItem>({
      getList,
      onFilterChange,
      onSortChange: () => {},
      schema: tariffsListSchema,
    });

  const onDeleteTariff = async () => {
    await deleteTariff(currentTariff?.id as string);
  };

  const onRowsSelect = (rows: string[]) => {
    if (!isEqual(selectedList, rows)) {
      dispatch(selectList(rows));

      if (rows.length === 1) {
        setCurrentTariff(tariffs?.find((item) => item.id === rows[0]));
      }

      const nextSelectedTariffs: TariffItem[] = [];

      rows.forEach((item) => {
        const currentSelected = tariffs?.find((tariff) => tariff.id === item);

        if (currentSelected) {
          nextSelectedTariffs.push(currentSelected);
        }
      });

      setSelectedTariffs(nextSelectedTariffs);
    }
  };

  const [changeStatus, { isLoading: statusChangeInProgress, isSuccess }] =
    useChangeTariffStatusMutation();

  const onStatusChange = async ({
    status,
    action,
    previousTariffId,
    nextTariffId,
  }: {
    status: TariffStatus;
    action?: TariffAction;
    previousTariffId?: string;
    nextTariffId?: string;
  }) => {
    const body: {
      status: TariffStatus;
      previousTariffId?: string;
      action?: TariffAction;
      nextTariffId?: string;
    } = { status };

    if (action) {
      body.action = action;
    }

    if (previousTariffId) {
      body.previousTariffId = previousTariffId;
    }

    if (nextTariffId) {
      body.nextTariffId = nextTariffId;
    }

    const response = (await changeStatus({ id: selectedList[0], body })) as {
      data: { data: TariffItem };
    };

    if (response && response.data) {
      setCurrentTariff(response.data.data);
    }
  };

  const onMakeDefault = () => {
    makeDefault({ id: defaultTariff?.id, body: { default: true } });
  };

  const duplicateTariff = () => {
    selectedList.forEach((item) => {
      duplicate(item);
    });
  };

  useScroll({ element: scrollRef, requestInProgress: isFetching });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: tariffsListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useEffect(() => {
    if (generalInfo) {
      dispatch(clearTariffPageState());
    }
  }, [generalInfo]);

  useEffect(() => {
    setValues(
      'riskScoreRangeId',
      riskScoreRanges?.map((item) => ({
        name: item.name,
        value: item.id,
        description: `from ${item.rangeStart} to ${item.rangeEnd}`,
      })) as SelectItemProps[],
    );
  }, [riskScoreRanges]);

  useEffect(() => {
    if (defaultIsSuccess) {
      setDefaultTariff({ popupVisible: false, id: '' });
    }
  }, [defaultIsSuccess]);

  const formattedData = useMemo(() => {
    const data = formatTariffsList(
      tariffs as TariffItem[],
      riskScoreRanges?.map((item) => ({
        name: item.name,
        value: item.id,
        description: `from ${item.rangeStart} to ${item.rangeEnd}`,
      })) as SelectItemProps[],
      setDefaultTariff,
    );

    return data;
  }, [tariffs, filters, riskScoreRanges]);

  useEffect(() => {
    if (!tariffs) {
      let currentFilters = filters;
      const currentFiltersStorage = localStorage.getItem('tariffsFilters');

      if (currentFiltersStorage) {
        currentFilters = JSON.parse(currentFiltersStorage);

        onFilterChange(currentFilters);
      }

      if (!currentFiltersStorage) {
        const filtersValues: Filters = { ...currentFilters };

        onFilter({ ...filtersValues });
      } else if (!tariffs) {
        getListAction({ filters: { ...currentFilters } });
      }
    }
  }, [filters, prevFilters, tariffs]);

  useEffect(() => {
    if (!tariffs && search) {
      formatSearchParams({
        currentSchemaValues: tariffsListSchema,
        storageKey: 'tariffsFilters',
        search,
      });
    }
  }, [tariffs, search]);

  useEffect(() => {
    if (deleteIsSuccess) {
      getListAction({ filters });

      setDeletePopupVisible(false);
    }
  }, [deleteIsSuccess, dispatch]);

  useEffect(() => {
    dispatch(clearTariffsListState());
  }, []);

  useEffect(() => {
    const currentFilters = localStorage.getItem('tariffsFilters');

    if (
      currentFilters &&
      !isEqual(prevPagination, pagination) &&
      !pagination?.hasPreviousPage
    ) {
      const parsed = JSON.parse(currentFilters);

      if (parsed.cursor) {
        delete parsed.cursor;

        localStorage.setItem('tariffsFilters', JSON.stringify({ ...parsed }));
      }
    }
  }, [pagination, prevPagination]);

  useEffect(() => {
    let title = 'Tariffs';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.tariffsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        take="20"
        setPage={(data) => {
          setPage({ ...data, filters });

          localStorage.setItem(
            'tariffsFilters',
            JSON.stringify({
              ...filters,
              cursor: { value: [data.id], type: SchemaFilterType.STRING },
            }),
          );
        }}
        pagination={pagination}
        columns={tariffsListColumns}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        error={errorsList || errorList}
        sort={{}}
        onRowsSelect={
          activePermissions?.includes(PermissionEnum.TARIFFS_EDIT_ACTION)
            ? onRowsSelect
            : undefined
        }
        columnsLabels={tariffsListColumnsLabels}
        onFilterChange={(filterValues) => {
          const nextFilters = { ...filterValues };

          delete nextFilters.cursor;

          onFilter(nextFilters);

          localStorage.setItem(
            'tariffsFilters',
            JSON.stringify({ ...nextFilters }),
          );
        }}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isFetching}
        clickable
      />
    );
  }

  return (
    <div className={cn.tariffsBottom} ref={scrollRef}>
      <div className={cn.tariffsTopWrapper}>
        <div className={cn.tariffsTitle}>Tariffs</div>
        <div className={cn.tariffsTop}>
          {activePermissions?.includes(
            PermissionEnum.TARIFFS_CREATE_ACTION,
          ) && (
            <NavLink
              to={`${TariffsRoutes.TARIFFS_CREATE_TARIFF}${search || ''}`}
              className={cn.tariffsCreateLink}
            >
              <div className={cn.tariffsCreateLinkContent}>
                <IconExpand className={cn.tariffsCreateLinkIcon} />
                <span>Add tariff</span>
              </div>
            </NavLink>
          )}

          {selectedList.length > 0 && (
            <>
              {selectedTariffs.filter(
                (item) => item.status === TariffStatus.DRAFT,
              ).length === 0 && (
                <>
                  <div className={cn.tariffsTopSeparator} />
                  <ButtonSecondary
                    className={`button-white ${cn.tariffsControlsButton}`}
                    size={ButtonSize.SMALL}
                    text={requestInProgress ? 'Loading...' : 'Duplicate'}
                    icon={<IconDuplicate />}
                    onClick={() => duplicateTariff()}
                  />
                </>
              )}

              {selectedList.length === 1 && (
                <>
                  {currentTariff?.status === TariffStatus.DRAFT && (
                    <>
                      <div className={cn.tariffsTopSeparator} />
                      <ButtonSecondary
                        className={`${cn.deleteButton} ${cn.tariffsControlsButton}`}
                        size={ButtonSize.SMALL}
                        icon={<IconTrash />}
                        text="Delete tariff"
                        onClick={() => setDeletePopupVisible(true)}
                      />
                    </>
                  )}
                  <ButtonSecondary
                    className={`button-white ${cn.tariffsControlsButton}`}
                    size={ButtonSize.SMALL}
                    text={
                      currentTariff?.status === TariffStatus.DRAFT ||
                      currentTariff?.status === TariffStatus.ARCHIVED
                        ? 'Publish'
                        : 'Archive'
                    }
                    onClick={() =>
                      currentTariff?.status === TariffStatus.DRAFT ||
                      currentTariff?.status === TariffStatus.ARCHIVED
                        ? setPublishPopupVisible(true)
                        : setArchivePopupVisible(true)
                    }
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
      {content}
      <Popup
        title="Delete draft tariff?"
        onCancelClick={() => setDeletePopupVisible(false)}
        onOkClick={onDeleteTariff}
        cancelButtonText="Cancel"
        okButtonText={deleteInProgress ? 'Loading...' : 'Delete'}
        isVisible={deletePopupVisible}
        warning
      />
      <Popup
        title="Make tariff default?"
        onCancelClick={() =>
          setDefaultTariff({ ...defaultTariff, popupVisible: false })
        }
        onOkClick={onMakeDefault}
        cancelButtonText="Cancel"
        okButtonText={defaultInProgress ? 'Loading...' : 'Make as default'}
        isVisible={defaultTariff.popupVisible}
      />
      <PublishSidepanel
        panelIsOpen={publishPopupVisible}
        setPanelIsOpen={setPublishPopupVisible}
        status={TariffStatus.PUBLISHED}
        onStatusChange={onStatusChange}
        statusChangeSuccess={isSuccess}
        tariffId={selectedList[0]}
        statusChangeInProgress={statusChangeInProgress}
      />
      <ArchiveSidepanel
        panelIsOpen={archivePopupVisible}
        setPanelIsOpen={setArchivePopupVisible}
        status={TariffStatus.ARCHIVED}
        onStatusChange={onStatusChange}
        statusChangeSuccess={isSuccess}
        statusChangeInProgress={statusChangeInProgress}
        tariffId={selectedList[0]}
      />

      <Outlet />
    </div>
  );
};

export default TariffsList;
